import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useColumnWidths, ResizeHandle, ScrollTableShell } from "@/components/common/ResizableTable";
import { viewAttachment, downloadAttachment } from "@/lib/files";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineSpinner } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { BookOpen, Plus, Search, MoreHorizontal, Eye, Download, FileText, Link2, Type, AlertTriangle, RefreshCw, Archive, Trash2, History, Globe, Building2, Layers, Sparkles, Upload, CheckCircle2, MapPin, CheckSquare, Square, Users, FolderInput, ShieldAlert, Pencil, Check, X, Tags, ClipboardCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AskKnowledge } from "@/components/knowledge/AskKnowledge";
import CertifiedSources from "@/pages/admin/CertifiedSources";
import { toast } from "sonner";
import { runKnowledgeAnalysis, applySuggestions } from "@/lib/knowledgeAnalysis";

const SCOPE_META = {
  system: { label: "System", icon: Globe, cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  account: { label: "All Groups / Districts", icon: Building2, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  group: { label: "Group", icon: Layers, cls: "bg-teal-50 text-teal-700 border-teal-200" },
};

export function AppliesTo({ scope, accountName, orgName, testId = "knowledge-applies-to" }) {
  const text = scope === "system" ? "Entire System"
    : scope === "account" ? `All Groups / Districts in ${accountName || "this account"}`
    : orgName ? `${orgName} only` : "Select a Group / District";
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid={testId}>
      <span className="font-semibold text-slate-700">Applies to: </span>{text}
      {scope === "account" && <p className="mt-0.5 text-[11px] text-slate-500">One shared source for every current and future district. State restrictions below still apply.</p>}
      {scope === "system" && <p className="mt-0.5 text-[11px] text-amber-700">Reserve System for genuinely system-wide Top-Recruit content — use All Groups / Districts for Farmers documents shared by your districts.</p>}
      {scope === "group" && accountName && <p className="mt-0.5 text-[11px] text-slate-500">Account: {accountName}</p>}
    </div>
  );
}

const audValue = (scope, org) => (scope === "group" ? (org ? `g:${org}` : "") : scope);
const audOptions = (scopes, orgs) => [
  ...(scopes.includes("account") ? [["account", "All Groups / Districts"]] : []),
  ...(scopes.includes("group") ? orgs.filter((o) => o.status !== "inactive").map((o) => [`g:${o.id}`, o.name]) : []),
  ...(scopes.includes("system") ? [["system", "Entire System (platform-wide)"]] : []),
];
const audParse = (v) => (v.startsWith("g:") ? ["group", v.slice(2)] : [v, ""]);
const defaultScope = (scopes) => (scopes.includes("account") ? "account" : scopes.includes("group") ? "group" : scopes[0] || "group");

export function AudienceSelect({ scopes, orgs, scope, orgId, onChange, testId = "knowledge-input-audience", small }) {
  return (
    <Select value={audValue(scope, orgId) || undefined} onValueChange={(v) => onChange(...audParse(v))}>
      <SelectTrigger className={small ? "h-7 text-xs" : "mt-1"} data-testid={testId}><SelectValue placeholder="Select Group / District" /></SelectTrigger>
      <SelectContent className="bg-white">{audOptions(scopes, orgs).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-opt-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  superseded: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
  expired: "bg-rose-50 text-rose-700 border-rose-200",
};
const TYPE_ICON = { document: FileText, text: Type, link: Link2 };
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const fileUrl = (id, version) => `/knowledge/sources/${id}/file${version ? `?version=${version}` : ""}`;

export default function KnowledgeCenter() {
  const { isSuper, isAdmin } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("all");
  const [fScope, setFScope] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fApplic, setFApplic] = useState("all");
  const [fYear, setFYear] = useState("all");
  const [fGroup, setFGroup] = useState("all");
  const [fCol, setFCol] = useState("all");
  const [fAud, setFAud] = useState("all");
  const [fState, setFState] = useState("all");
  const [fCert, setFCert] = useState("all");
  const [groups, setGroups] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [versioning, setVersioning] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [replacing, setReplacing] = useState(null);
  const [history, setHistory] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [tab, setTab] = useState("sources");
  const [sel, setSel] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null);

  const loadMeta = useCallback(async () => {
    try { const { data } = await api.get("/knowledge/meta"); setMeta(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (fCat !== "all") params.category = fCat;
      if (fScope !== "all") params.scope = fScope;
      if (fStatus !== "all") params.status = fStatus;
      if (fApplic !== "all") params.applicability = fApplic;
      if (fYear !== "all") params.year = fYear;
      if (fGroup !== "all") params.group_id = fGroup;
      if (fCol !== "all") params.collection = fCol;
      if (fAud !== "all") params.audience = fAud;
      if (fState !== "all") params.state_filter = fState;
      if (fCert !== "all") params.certified = fCert;
      const { data } = await api.get("/knowledge/sources", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, fCat, fScope, fStatus, fApplic, fYear, fGroup, fCol, fAud, fState, fCert]);
  const anyFilter = [fCat, fScope, fStatus, fApplic, fYear, fGroup, fCol, fAud, fState, fCert].some((v) => v !== "all") || q;
  const clearFilters = () => { [setFCat, setFScope, setFStatus, setFApplic, setFYear, setFGroup, setFCol, setFAud, setFState, setFCert].forEach((f) => f("all")); setQ(""); };

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => {
    api.get("/organizations").then(({ data }) => setGroups(data.organizations || [])).catch(() => {});
  }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  // keep selection consistent with what is currently visible
  useEffect(() => { setSel((s) => new Set([...s].filter((id) => items.some((i) => i.id === id)))); }, [items]);

  const counts = meta?.counts || {};
  const [reviewList, setReviewList] = useState([]);
  const loadReview = useCallback(async () => {
    try { const { data } = await api.get("/knowledge/review"); setReviewList(data.items || []); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { loadReview(); }, [loadReview, items]);

  const refreshAll = () => { load(); loadMeta(); loadReview(); };

  const setStatus = async (s, status) => {
    try {
      if (status === "archived") await api.post(`/knowledge/sources/${s.id}/archive`, {});
      else await api.post(`/knowledge/sources/${s.id}/status`, { status });
      toast.success(`Marked ${status}`); refreshAll();
    } catch (e) { toast.error(apiError(e)); }
  };
  const restore = async (s) => {
    try { const { data } = await api.post(`/knowledge/sources/${s.id}/restore`); toast.success(data.conflict ? "Restored as Draft (version conflict — review)" : "Restored"); refreshAll(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (s) => {
    const published = ["published", "superseded", "expired"].includes(s.status) || s.version > 1;
    if (published && !isSuper) { toast.error("Archive this source instead of deleting."); return; }
    const force = published && isSuper;
    if (!window.confirm(force ? `Permanently delete "${s.display_title || s.title}"? This is an exceptional action.` : `Delete "${s.display_title || s.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/knowledge/sources/${s.id}${force ? "?force=true&reason=admin-delete" : ""}`); toast.success("Deleted"); refreshAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // selection helpers
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((i) => sel.has(i.id));
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  const clearSel = () => setSel(new Set());
  const selItems = items.filter((i) => sel.has(i.id));
  const allManage = selItems.length > 0 && selItems.every((i) => i.can_manage !== false);

  return (
    <div data-testid="knowledge-center">
      <PageHeader title="Knowledge Center" description="Authoritative reference documents used within Top-Recruit — the foundation for future AI search and citations."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setBulk(true)} data-testid="knowledge-bulk-upload"><Upload className="w-4 h-4 mr-1.5" /> Bulk Upload</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" onClick={() => setAdding(true)} data-testid="knowledge-add-source"><Plus className="w-4 h-4 mr-1.5" /> Add Source</Button>
        </div>} />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[["Published", counts.published, "text-emerald-600"], ["Needs Review", counts.needs_review, "text-amber-600"],
          ["Drafts", counts.draft, "text-slate-600"], ["Archived", counts.archived, "text-slate-500"]].map(([l, v, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`knowledge-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-xs text-slate-400 font-medium">{l}</p>
            <p className={`text-2xl font-bold ${c} tabular-nums`}>{v ?? 0}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="knowledge-tabs" className="h-auto flex-wrap justify-start">
          <TabsTrigger value="sources" data-testid="knowledge-tab-sources">Sources</TabsTrigger>
          <TabsTrigger value="collections" data-testid="knowledge-tab-collections">Collections</TabsTrigger>
          <TabsTrigger value="review" data-testid="knowledge-tab-review">Review Queue{reviewList.length ? ` (${reviewList.length})` : ""}</TabsTrigger>
          <TabsTrigger value="certified" data-testid="knowledge-tab-certified">Certified Sources</TabsTrigger>
          <TabsTrigger value="assistant" data-testid="knowledge-tab-assistant">Assistant</TabsTrigger>
          <TabsTrigger value="settings" data-testid="knowledge-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="mt-4">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, keywords, content…" className="pl-9" data-testid="knowledge-search" />
            </div>
            <FilterSelect value={fYear} onChange={setFYear} testId="knowledge-filter-year" all="All years"
              options={(meta?.years || []).map((y) => [String(y), String(y)])} />
            <FilterSelect value={fScope} onChange={setFScope} testId="knowledge-filter-scope" all="All Scopes"
              options={[["system", "System"], ["account", "Account"], ["group", "Group"]]} />
            <FilterSelect value={fGroup} onChange={setFGroup} testId="knowledge-filter-group" all="All Groups / Districts"
              options={groups.map((o) => [o.id, o.name])} />
            <FilterSelect value={fCat} onChange={setFCat} testId="knowledge-filter-category" all="All categories"
              options={(meta?.categories || []).map((c) => [c.label, c.label])} />
            <FilterSelect value={fStatus} onChange={setFStatus} testId="knowledge-filter-status" all="All statuses"
              options={[["current", "Current"], ...(meta?.statuses || []).map((s) => [s, s[0].toUpperCase() + s.slice(1)])]} />
            <FilterSelect value={fApplic} onChange={setFApplic} testId="knowledge-filter-applicability" all="Any applicability"
              options={[["my_states", "My States"], ["all_states", "All States"], ["not_state_specific", "Not State-Specific"]]} />
            <FilterSelect value={fCol} onChange={setFCol} testId="knowledge-filter-collection" all="All collections"
              options={[...(meta?.collections || []).map((c) => [c.key, c.label]), ["none", "No collection"]]} />
            <FilterSelect value={fAud} onChange={setFAud} testId="knowledge-filter-audience" all="All audiences"
              options={(meta?.audiences || []).map((a) => [a.key, a.label])} />
            <FilterSelect value={fState} onChange={setFState} testId="knowledge-filter-state" all="All states"
              options={(meta?.states || []).map((s) => [s.code, `${s.name} (${s.code})`])} />
            <FilterSelect value={fCert} onChange={setFCert} testId="knowledge-filter-certified" all="Certified or not"
              options={[["yes", "Certified only"], ["no", "Not certified"]]} />
            {anyFilter && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="knowledge-filter-clear">Clear filters</Button>}
          </div>
          <p className="text-xs text-slate-400 -mt-2 mb-3" data-testid="knowledge-result-count">{loading ? "Loading…" : `${items.length} source${items.length === 1 ? "" : "s"}`}</p>

          {sel.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3 rounded-xl border border-[#006090]/30 bg-[#006090]/5 px-3 py-2" data-testid="knowledge-bulk-bar">
              <span className="text-sm font-semibold text-[#006090]" data-testid="bulk-selected-count">{sel.size} selected</span>
              <span className="mx-1 h-4 w-px bg-slate-300" />
              {allManage && <Button size="sm" variant="outline" onClick={() => setBulkMode("archive")} data-testid="bulk-archive-btn"><Archive className="w-3.5 h-3.5 mr-1" />Archive</Button>}
              {allManage && <Button size="sm" variant="outline" onClick={() => setBulkMode("restore")} data-testid="bulk-restore-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Restore</Button>}
              {allManage && <Button size="sm" variant="outline" onClick={() => setBulkMode("collection")} data-testid="bulk-collection-btn"><FolderInput className="w-3.5 h-3.5 mr-1" />Collection</Button>}
              {allManage && <Button size="sm" variant="outline" onClick={() => setBulkMode("category")} data-testid="bulk-category-btn"><Tags className="w-3.5 h-3.5 mr-1" />Category</Button>}
              {allManage && <Button size="sm" variant="outline" onClick={() => setBulkMode("applicability")} data-testid="bulk-applic-btn"><MapPin className="w-3.5 h-3.5 mr-1" />Applicability</Button>}
              {allManage && <Button size="sm" variant="outline" onClick={() => setBulkMode("audience")} data-testid="bulk-audience-btn"><Users className="w-3.5 h-3.5 mr-1" />Audience</Button>}
              {allManage && <Button size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setBulkMode("delete")} data-testid="bulk-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete</Button>}
              {!allManage && <span className="text-xs text-amber-600">Some selected sources are outside your management scope.</span>}
              <Button size="sm" variant="ghost" onClick={clearSel} className="ml-auto" data-testid="bulk-clear-btn">Clear</Button>
            </div>
          )}

          <SourceTable items={items} loading={loading} onEdit={setEditing} onVersion={setVersioning}
            onStatus={setStatus} onDelete={del} onReplace={setReplacing} onHistory={setHistory} onRestore={restore}
            sel={sel} onToggle={toggle} allSelected={allSelected} onToggleAll={toggleAll}
            meta={meta} onSaved={refreshAll}
            onAdd={() => setAdding(true)} onBulk={() => setBulk(true)} />
        </TabsContent>

        <TabsContent value="collections" className="mt-4">
          <Collections meta={meta} canManage={isSuper || isAdmin} onChanged={loadMeta} />
        </TabsContent>

        <TabsContent value="review" className="mt-4">
          {reviewList.length === 0
            ? <EmptyState icon={BookOpen} title="Nothing needs review" description="Conflicts, missing applicability/metadata, unverified years, extraction errors and due/expired sources appear here." />
            : <ReviewQueue items={reviewList} onEdit={setEditing} />}
        </TabsContent>

        <TabsContent value="certified" className="mt-4">
          <CertifiedSources />
        </TabsContent>

        <TabsContent value="assistant" className="mt-4">
          <div className="max-w-2xl">
            <AskKnowledge />
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />Answers are grounded strictly on published sources you can access, with citations. Powered by Claude Sonnet 4.6.</p>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-4 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-6 max-w-2xl space-y-4">
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">Scope & permissions</h3>
              <p className="text-sm text-slate-500 mt-1">You can manage these scopes: {" "}
                {(meta?.manageable_scopes || []).map((s) => SCOPE_META[s]?.label).join(", ") || "none"}.</p>
              <ul className="text-xs text-slate-500 mt-3 space-y-1 list-disc pl-5">
                <li><strong>System</strong> — authoritative global library (Platform Super Admin).</li>
                <li><strong>Account</strong> — knowledge for your Client Account (Account Admin).</li>
                <li><strong>Group</strong> — supplemental District/Group material (Group Admin, own districts).</li>
              </ul>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <h3 className="font-semibold text-slate-800 text-sm">Library health</h3>
              <div className="grid grid-cols-3 gap-3 mt-2 text-sm">
                {["published", "draft", "superseded", "archived", "expired", "total"].map((k) => (
                  <div key={k} className="rounded-lg bg-slate-50 px-3 py-2">
                    <span className="text-slate-400 text-xs capitalize">{k}</span>
                    <p className="font-semibold text-slate-700 tabular-nums">{counts[k] ?? 0}</p>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-400 border-t border-slate-100 pt-4">
              Uploaded documents are stored securely and their readable text is extracted for future Top-Recruit AI search and source citations. Originals are always preserved. Display Titles are generated as <span className="font-mono">YYYY Official Title</span> from the authoritative document date.
            </p>
          </div>

          {isSuper && (
            <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-6 max-w-2xl" data-testid="knowledge-danger-zone">
              <h3 className="font-semibold text-rose-700 text-sm flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" />Danger Zone — Reset Preview Library</h3>
              <p className="text-sm text-slate-600 mt-1">Permanently removes <strong>all</strong> Preview Knowledge sources &amp; files so you can start real uploads from an empty library. Collections, taxonomy, RBAC, Assistant and Settings are kept intact. Preview only — Production is never affected.</p>
              <Button variant="outline" className="mt-3 text-rose-600 border-rose-300 hover:bg-rose-100" onClick={() => setResetting(true)} data-testid="knowledge-reset-preview">Reset Preview Library…</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {adding && <SourceDialog meta={meta} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refreshAll(); }} />}
      {editing && <SourceDialog meta={meta} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refreshAll(); }} />}
      {versioning && <VersionDialog source={versioning} onClose={() => setVersioning(null)} onSaved={() => { setVersioning(null); load(); loadMeta(); }} />}
      {bulk && <BulkUploadDialog meta={meta} onClose={() => setBulk(false)} onDone={refreshAll} />}
      {replacing && <ReplaceFileDialog source={replacing} onClose={() => setReplacing(null)} onSaved={() => { setReplacing(null); load(); loadMeta(); }} />}
      {history && <FileHistoryDialog source={history} onClose={() => setHistory(null)} />}
      {bulkMode && <BulkDialog mode={bulkMode} items={selItems} meta={meta} isSuper={isSuper}
        onClose={() => setBulkMode(null)} onDone={() => { setBulkMode(null); clearSel(); refreshAll(); }} />}
      {resetting && <ResetPreviewDialog onClose={() => setResetting(false)} onDone={() => { setResetting(false); refreshAll(); }} />}
    </div>
  );
}

function FilterSelect({ value, onChange, options, all, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[150px]" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-white">
        <SelectItem value="all">{all}</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ReviewAuditCell({ s, canManage, onSaved }) {
  const name = s.updated_by_name || s.uploaded_by_name;
  const verb = s.updated_by_name && s.updated_at !== s.created_at ? "Updated" : "Added";
  const when = fmtDate(s.updated_at || s.created_at);
  const act = async (reviewed) => {
    try { await api.post(`/knowledge/sources/${s.id}/review`, { reviewed }); toast.success(reviewed ? "Marked Reviewed" : "Flagged for review"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-left text-xs leading-tight hover:opacity-80" data-testid={`knowledge-audit-${s.id}`}>
          {s.needs_review
            ? <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><AlertTriangle className="w-3 h-3" />Needs Review</span>
            : s.reviewed_by_name
              ? <span className="text-emerald-700">Reviewed · {fmtDate(s.reviewed_at)}</span>
              : <span className="text-slate-500">{verb} {when}</span>}
          <span className="block text-slate-400 truncate max-w-[130px]">{s.needs_review && name ? `by ${name}` : (s.reviewed_by_name || name || "—")}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="bg-white w-64 text-xs space-y-1.5" data-testid={`knowledge-audit-pop-${s.id}`}>
        <p className="font-semibold text-slate-700 text-sm">Review / Audit</p>
        {s.uploaded_by_name && <DRow k="Added by" v={`${s.uploaded_by_name} · ${fmtDate(s.uploaded_at || s.created_at)}`} />}
        {s.updated_by_name && <DRow k="Updated by" v={`${s.updated_by_name} · ${fmtDate(s.updated_at)}`} />}
        {s.reviewed_by_name && <DRow k="Reviewed by" v={`${s.reviewed_by_name} · ${fmtDate(s.reviewed_at)}`} />}
        {s.needs_review && s.review_reason && <DRow k="Reason" v={s.review_reason} />}
        {canManage && (
          <div className="pt-1.5 flex gap-1.5">
            {s.needs_review
              ? <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act(true)} data-testid={`knowledge-markreviewed-${s.id}`}><ClipboardCheck className="w-3 h-3 mr-1" />Mark Reviewed</Button>
              : <Button size="sm" variant="outline" className="h-7" onClick={() => act(false)} data-testid={`knowledge-flagreview-${s.id}`}>Flag for review</Button>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function InlineSelect({ value, onChange, options, placeholder, testId }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs min-w-[120px]" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white">{options.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

const KC_HEADERS = ["Source", "Year", "Scope", "Applicability", "Collection", "Category", "Type", "Status", "Ver", "Effective", "Review / Audit", ""];
const KC_COL_WIDTHS = [44, 320, 72, 130, 150, 170, 170, 140, 110, 64, 116, 180, 96];

function SourceTable({ items, loading, onEdit, onVersion, onStatus, onDelete, onReplace, onHistory, onRestore, sel, onToggle, allSelected, onToggleAll, onAdd, onBulk, meta, onSaved }) {
  const cols = useColumnWidths("knowledge-sources-columns", KC_COL_WIDTHS);
  const [editId, setEditId] = useState(null);
  const [rf, setRf] = useState({});
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const scopes = meta?.manageable_scopes || [];
  const dirty = editId && JSON.stringify(rf) !== JSON.stringify(orig);

  useEffect(() => {
    if (scopes.includes("group") && orgs.length === 0) api.get("/organizations").then(({ data }) => setOrgs(data.organizations || [])).catch(() => {});
  }, [editId]); // eslint-disable-line

  useEffect(() => {
    if (!editId || !dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [editId, dirty]);

  const open = async (s, version) => {
    try { await viewAttachment(fileUrl(s.id, version), s.original_filename || s.title); }
    catch { toast.error("Could not open file"); }
  };
  const dl = async (s) => {
    try { await downloadAttachment(fileUrl(s.id), s.original_filename || s.title); }
    catch { toast.error("Could not download file"); }
  };

  const guard = () => !dirty || window.confirm("You have unsaved changes. Discard them?");
  const startEdit = (s) => {
    if (editId && !guard()) return;
    const f = {
      display_title: s.display_title || s.title || "", document_year: s.document_year || "",
      scope_type: s.scope_type || "account", organization_id: s.organization_id || "",
      applicability_mode: s.applicability_mode || "unknown",
      applicable_states: s.applicable_states || [], excluded_states: s.excluded_states || [],
      collection: s.collection || "", category: s.category || "", document_type: s.document_type || "",
      status: s.status || "draft", version: s.version || 1, effective_date: (s.effective_date || "").slice(0, 10),
    };
    setEditId(s.id); setRf(f); setOrig(f);
  };
  const cancel = () => { if (guard()) { setEditId(null); setRf({}); setOrig({}); } };
  const set = (k, v) => setRf((p) => ({ ...p, [k]: v }));
  const toggleSt = (code, key) => setRf((p) => { const a = new Set(p[key] || []); a.has(code) ? a.delete(code) : a.add(code); return { ...p, [key]: [...a] }; });

  const save = async (s) => {
    setBusy(true);
    try {
      const changes = {};
      Object.keys(rf).forEach((k) => { if (JSON.stringify(rf[k]) !== JSON.stringify(orig[k])) changes[k] = rf[k]; });
      if (changes.document_year === "") changes.document_year = null;
      if (changes.version !== undefined) changes.version = Number(changes.version);
      await api.patch(`/knowledge/sources/${s.id}`, changes);
      toast.success("Saved");
      setEditId(null); setRf({}); setOrig({}); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (loading && !items.length) return <div className="py-16 flex justify-center"><InlineSpinner /></div>;
  if (!items.length) return (
    <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="knowledge-empty-state">
      <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <h3 className="text-lg font-semibold text-slate-800">No Sources yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Upload your first authoritative document to begin building your Knowledge Library.</p>
      <div className="flex justify-center gap-2 mt-4">
        <Button className="bg-[#006090] hover:bg-[#004E75]" onClick={onAdd} data-testid="empty-add-source"><Plus className="w-4 h-4 mr-1.5" />Add Source</Button>
        <Button variant="outline" onClick={onBulk} data-testid="empty-bulk-upload"><Upload className="w-4 h-4 mr-1.5" />Bulk Upload</Button>
      </div>
    </div>
  );
  const catOpts = (meta?.categories || []).map((c) => [c.label, c.label]);
  const colOpts = (meta?.collections || []).map((c) => [c.key, c.label]);
  const typeOpts = (meta?.document_types || []).map((t) => [t, t]);
  const statusOpts = (meta?.statuses || []).map((s) => [s, s[0].toUpperCase() + s.slice(1)]);
  const scopeOpts = scopes.map((s) => [s, SCOPE_META[s]?.label || s]);
  const applicOpts = (meta?.applicability_modes || []).map((m) => [m.key, m.label]);

  const W = cols.widths;
  const F0 = "md:sticky md:left-0 md:z-10";
  const F1 = "md:sticky md:z-10 md:left-[var(--l)] md:shadow-[1px_0_0_#E2E8F0]";
  const L1 = { "--l": `${W[0]}px` };
  const FR = "md:sticky md:right-0 md:z-10 md:shadow-[-1px_0_0_#E2E8F0]";
  return (
    <ScrollTableShell cols={cols} testId="knowledge-table-shell" viewsKey="knowledge-sources-columns-views">
      <table className="text-sm [&_td]:overflow-hidden [&_td]:text-ellipsis [&_td_.whitespace-nowrap]:truncate" style={{ tableLayout: "fixed", width: cols.total, minWidth: "100%" }} data-testid="knowledge-table">
        <colgroup>{W.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
        <thead>
          <tr className="text-left text-xs font-semibold text-slate-500">
            <th className={`sticky top-0 z-20 bg-slate-50 border-b border-slate-200 px-3 py-2.5 ${F0} md:z-30`}>
              <button onClick={onToggleAll} data-testid="knowledge-select-all" className="text-slate-400 hover:text-[#006090]" aria-label="Select all">
                {allSelected ? <CheckSquare className="w-4 h-4 text-[#006090]" /> : <Square className="w-4 h-4" />}
              </button>
            </th>
            {KC_HEADERS.map((h, j) => {
              const i = j + 1;
              const frozen = i === 1 ? `${F1} md:z-30` : i === KC_HEADERS.length ? `${FR} md:z-30` : "";
              return (
                <th key={h || "actions"} className={`relative sticky top-0 z-20 bg-slate-50 border-b border-slate-200 px-3 py-2.5 whitespace-nowrap overflow-hidden text-ellipsis ${frozen}`}
                  style={i === 1 ? L1 : undefined} data-testid={`knowledge-col-${i}`}>
                  {h || <span className="sr-only">Actions</span>}
                  {h && <ResizeHandle index={i} width={W[i]} onResize={cols.setWidth} label={h} />}
                </th>);
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((s) => {
            const Sc = SCOPE_META[s.scope_type] || SCOPE_META.account;
            const TypeIcon = TYPE_ICON[s.source_type] || FileText;
            const hasFile = s.source_type === "document";
            const mng = s.can_manage !== false;
            const checked = sel.has(s.id);
            const editing = editId === s.id;
            if (editing) {
              return (
                <tr key={s.id} className="border-b border-slate-100 align-top bg-amber-50/40" data-testid={`knowledge-row-${s.id}`}>
                  <td className={`px-3 py-3 bg-amber-50 ${F0}`} />
                  <td className={`px-3 py-3 bg-amber-50 ${F1}`} style={L1}><Input value={rf.display_title} onChange={(e) => set("display_title", e.target.value)} className="h-8 text-xs" data-testid={`inline-title-${s.id}`} /></td>
                  <td className="px-3 py-3"><Input value={rf.document_year} onChange={(e) => set("document_year", e.target.value)} className="h-8 text-xs w-16" data-testid={`inline-year-${s.id}`} /></td>
                  <td className="px-3 py-3">
                    <AudienceSelect small scopes={scopes} orgs={orgs} scope={rf.scope_type} orgId={rf.organization_id}
                      onChange={(sc, o) => setRf((p) => ({ ...p, scope_type: sc, organization_id: o }))} testId={`inline-scope-${s.id}`} />
                  </td>
                  <td className="px-3 py-3">
                    <InlineSelect value={rf.applicability_mode} onChange={(v) => set("applicability_mode", v)} options={applicOpts} testId={`inline-applic-${s.id}`} />
                    {(rf.applicability_mode === "selected_states" || rf.applicability_mode === "all_except") && (
                      <Popover><PopoverTrigger asChild><Button size="sm" variant="outline" className="h-7 mt-1 text-xs" data-testid={`inline-states-${s.id}`}>{(rf.applicability_mode === "selected_states" ? rf.applicable_states : rf.excluded_states).length || 0} states</Button></PopoverTrigger>
                        <PopoverContent className="bg-white w-56"><div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto tr-scroll">
                          {(meta?.states || []).map((st) => { const key = rf.applicability_mode === "selected_states" ? "applicable_states" : "excluded_states"; const on = (rf[key] || []).includes(st.code); return <button key={st.code} type="button" onClick={() => toggleSt(st.code, key)} className={`text-[11px] rounded border px-1.5 py-0.5 ${on ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{st.code}</button>; })}
                        </div></PopoverContent></Popover>
                    )}
                  </td>
                  <td className="px-3 py-3"><InlineSelect value={rf.collection} onChange={(v) => set("collection", v)} options={colOpts} placeholder="—" testId={`inline-collection-${s.id}`} /></td>
                  <td className="px-3 py-3"><InlineSelect value={rf.category} onChange={(v) => set("category", v)} options={catOpts} placeholder="—" testId={`inline-category-${s.id}`} /></td>
                  <td className="px-3 py-3"><InlineSelect value={rf.document_type} onChange={(v) => set("document_type", v)} options={typeOpts} placeholder="—" testId={`inline-type-${s.id}`} /></td>
                  <td className="px-3 py-3"><InlineSelect value={rf.status} onChange={(v) => set("status", v)} options={statusOpts} testId={`inline-status-${s.id}`} /></td>
                  <td className="px-3 py-3"><Input type="number" min="1" value={rf.version} onChange={(e) => set("version", e.target.value)} className="h-8 text-xs w-14" data-testid={`inline-version-${s.id}`} /></td>
                  <td className="px-3 py-3"><Input type="date" value={rf.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="h-8 text-xs" data-testid={`inline-effective-${s.id}`} /></td>
                  <td className="px-3 py-3 text-xs text-slate-400">—</td>
                  <td className={`px-3 py-3 bg-amber-50 ${FR}`}>
                    <div className="flex gap-1">
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white px-2" disabled={busy} onClick={() => save(s)} data-testid={`inline-save-${s.id}`}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={cancel} data-testid={`inline-cancel-${s.id}`}><X className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              );
            }
            return (
              <tr key={s.id} className={`border-b border-slate-100 align-top ${checked ? "bg-[#006090]/5" : ""}`} data-testid={`knowledge-row-${s.id}`}>
                <td className={`px-3 py-3 ${checked ? "bg-sky-50" : "bg-white"} ${F0}`}>
                  <button onClick={() => onToggle(s.id)} data-testid={`knowledge-select-${s.id}`} className="text-slate-400 hover:text-[#006090]" aria-label="Select row">
                    {checked ? <CheckSquare className="w-4 h-4 text-[#006090]" /> : <Square className="w-4 h-4" />}
                  </button>
                </td>
                <td className={`px-3 py-3 ${checked ? "bg-sky-50" : "bg-white"} ${F1}`} style={L1}>
                  <div className="flex items-start gap-2">
                    <TypeIcon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <button onClick={() => hasFile ? open(s) : (s.link_url ? window.open(s.link_url, "_blank", "noopener") : onEdit(s))} className="font-medium text-slate-800 hover:text-[#006090] text-left line-clamp-2 break-words max-w-full" title={s.display_title || s.title} data-testid={`knowledge-title-${s.id}`}>{s.display_title || s.title}</button>
                      <p className="text-xs text-slate-400 truncate">{s.human_id}{s.document_number ? ` · ${s.document_number}` : ""}{s.original_filename ? ` · ${s.original_filename}` : ""}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-slate-600 tabular-nums whitespace-nowrap" data-testid={`knowledge-year-${s.id}`}>{s.document_year || "—"}</td>
                <td className="px-3 py-3"><Badge variant="outline" className={`${Sc.cls} whitespace-nowrap`} data-testid={`knowledge-scope-${s.id}`}>{s.scope_type === "account" ? "All Districts" : Sc.label}</Badge>
                  {s.scope_type === "group" && <p className="mt-1 text-[10px] text-slate-500" data-testid={`knowledge-group-${s.id}`}>{s.organization_name || "Group / District not set"}</p>}
                  {s.scope_issue && <Badge variant="outline" title={s.scope_issue} className="ml-1 text-[10px] border-amber-300 text-amber-700" data-testid={`knowledge-scope-issue-${s.id}`}>Review scope</Badge>}</td>
                <td className="px-3 py-3 whitespace-nowrap"><ApplicabilityBadge s={s} />
                  {(s.applicable_programs || []).length > 0 && <p className="mt-1 text-[10px] text-[#0D9488]" data-testid={`knowledge-programs-${s.id}`}>{s.applicable_programs.map((k) => (meta?.programs || []).find((p) => p.key === k)?.label || k).join(", ")}</p>}</td>
                <td className="px-3 py-3 text-slate-600 whitespace-nowrap text-xs">{COLLECTION_LABEL[s.collection] || "—"}</td>
                <td className="px-3 py-3 text-slate-600 whitespace-nowrap text-xs" data-testid={`knowledge-category-${s.id}`}>{s.category || "—"}</td>
                <td className="px-3 py-3 text-slate-500 whitespace-nowrap text-xs">{s.document_type || "—"}</td>
                <td className="px-3 py-3"><Badge variant="outline" className={STATUS_CLS[s.status]}>{s.status}</Badge></td>
                <td className="px-3 py-3 text-slate-500 tabular-nums">v{s.version}</td>
                <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{fmtDate(s.effective_date)}</td>
                <td className="px-3 py-3 whitespace-nowrap"><ReviewAuditCell s={s} canManage={mng} onSaved={onSaved} /></td>
                <td className={`px-3 py-3 text-right ${checked ? "bg-sky-50" : "bg-white"} ${FR}`}>
                  <div className="flex items-center justify-end gap-1">
                    {mng && <button onClick={() => startEdit(s)} data-testid={`knowledge-rowedit-${s.id}`} className="p-1 rounded hover:bg-slate-100" title="Edit row"><Pencil className="w-4 h-4 text-slate-500" /></button>}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><button data-testid={`knowledge-actions-${s.id}`} className="p-1 rounded hover:bg-slate-100"><MoreHorizontal className="w-4 h-4 text-slate-500" /></button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        {hasFile && <DropdownMenuItem onClick={() => open(s)} data-testid={`knowledge-view-${s.id}`}><Eye className="w-4 h-4 mr-2" />View</DropdownMenuItem>}
                        {hasFile && <DropdownMenuItem onClick={() => dl(s)} data-testid={`knowledge-download-${s.id}`}><Download className="w-4 h-4 mr-2" />Download</DropdownMenuItem>}
                        {s.link_url && <DropdownMenuItem onClick={() => window.open(s.link_url, "_blank", "noopener")}><Link2 className="w-4 h-4 mr-2" />Open link</DropdownMenuItem>}
                        {mng && <DropdownMenuItem onClick={() => startEdit(s)} data-testid={`knowledge-rowedit-menu-${s.id}`}><Pencil className="w-4 h-4 mr-2" />Edit row</DropdownMenuItem>}
                        {mng && <DropdownMenuItem onClick={() => onEdit(s)} data-testid={`knowledge-edit-${s.id}`}>Edit full metadata</DropdownMenuItem>}
                        {mng && hasFile && <DropdownMenuItem onClick={() => onReplace(s)} data-testid={`knowledge-replace-${s.id}`}><RefreshCw className="w-4 h-4 mr-2" />Replace file</DropdownMenuItem>}
                        {mng && hasFile && <DropdownMenuItem onClick={() => onVersion(s)} data-testid={`knowledge-version-${s.id}`}><History className="w-4 h-4 mr-2" />Upload new version</DropdownMenuItem>}
                        {hasFile && <DropdownMenuItem onClick={() => onHistory(s)} data-testid={`knowledge-history-${s.id}`}><History className="w-4 h-4 mr-2" />File history</DropdownMenuItem>}
                        {mng && <DropdownMenuSeparator />}
                        {mng && s.status !== "published" && s.status !== "archived" && <DropdownMenuItem onClick={() => onStatus(s, "published")} data-testid={`knowledge-publish-${s.id}`} className="text-emerald-700"><RefreshCw className="w-4 h-4 mr-2" />Publish</DropdownMenuItem>}
                        {mng && s.status === "published" && <DropdownMenuItem onClick={() => onStatus(s, "draft")} data-testid={`knowledge-unpublish-${s.id}`}>Unpublish (Draft)</DropdownMenuItem>}
                        {mng && s.status !== "archived" && <DropdownMenuItem onClick={() => onStatus(s, "archived")} data-testid={`knowledge-archive-${s.id}`}><Archive className="w-4 h-4 mr-2" />Archive / Retire</DropdownMenuItem>}
                        {mng && s.status === "archived" && <DropdownMenuItem onClick={() => onRestore(s)} data-testid={`knowledge-restore-${s.id}`}><RefreshCw className="w-4 h-4 mr-2" />Restore</DropdownMenuItem>}
                        {mng && s.status !== "published" && <DropdownMenuItem onClick={() => onDelete(s)} className="text-rose-600" data-testid={`knowledge-delete-${s.id}`}><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollTableShell>
  );
}

const F = ({ label, children, hint, span }) => (
  <div className={span ? "col-span-2" : ""}><Label className="text-xs font-semibold text-slate-700">{label}</Label>{children}{hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}</div>
);

const COLLECTION_LABEL = {
  recruiting_agency_ownership: "Recruiting & Agency Ownership", programs_pathways: "Programs & Pathways",
  contracts_agreements: "Contracts & Agreements", licensing_appointments: "Licensing & Appointments",
  field_bulletins: "Field Bulletins", training_development: "Training & Development",
  agency_operations: "Agency Operations", sales_marketing_growth: "Sales & Marketing",
  compliance_legal: "Compliance & Legal", forms_reference: "Forms & Reference", technology_systems: "Technology & Systems",
};
const APPLIC_LABEL = { all_states: "ALL STATES", not_state_specific: "Not State-Specific", unknown: "Needs Review" };

function ConfidenceBadge({ level, ai }) {
  if (!level) return null;
  const cls = level === "high" ? "border-emerald-200 text-emerald-700 bg-emerald-50"
    : level === "medium" ? "border-amber-200 text-amber-700 bg-amber-50"
    : "border-rose-200 text-rose-700 bg-rose-50";
  return (
    <span className="inline-flex items-center gap-1" data-testid="knowledge-confidence">
      <Badge variant="outline" className={`text-[10px] ${cls}`}>{level.toUpperCase()}</Badge>
      {ai && <Badge variant="outline" className="text-[10px] border-violet-200 text-violet-700 bg-violet-50"><Sparkles className="w-2.5 h-2.5 mr-0.5" />AI</Badge>}
    </span>
  );
}
const DRow = ({ k, v, mono }) => (v || v === 0 ? <div className="flex gap-2"><span className="text-slate-400 min-w-[130px]">{k}</span><span className={`text-slate-700 ${mono ? "font-mono break-all" : ""}`}>{v}</span></div> : null);

function ApplicabilityBadge({ s }) {
  const mode = s.applicability_mode;
  if (!mode) return <span className="text-slate-400 text-xs">—</span>;
  if (mode === "selected_states")
    return <span className="flex flex-wrap gap-0.5 max-w-[150px]">{(s.applicable_states || []).map((c) => <Badge key={c} variant="outline" className="text-[10px] border-sky-200 text-sky-700 px-1">{c}</Badge>)}</span>;
  if (mode === "all_except")
    return <span className="text-xs text-amber-700">All except {(s.excluded_states || []).join(", ")}</span>;
  const cls = mode === "all_states" ? "border-emerald-200 text-emerald-700" : mode === "unknown" ? "border-amber-200 text-amber-700" : "border-slate-200 text-slate-500";
  return <Badge variant="outline" className={`text-[10px] ${cls}`}>{APPLIC_LABEL[mode] || mode}</Badge>;
}


function SourceDialog({ meta, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const scopes = meta?.manageable_scopes || ["account"];
  const [sourceType, setSourceType] = useState(initial?.source_type || "document");
  const [scope, setScope] = useState(initial?.scope_type || defaultScope(scopes));
  const [orgId, setOrgId] = useState(initial?.organization_id || "");
  const [orgs, setOrgs] = useState([]);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: initial?.title || "", description: initial?.description || "", category: initial?.category || "",
    topic: initial?.topic || "", purpose: initial?.purpose || "", keywords: (initial?.keywords || []).join(", "),
    publisher: initial?.publisher || "", applicable_state: initial?.applicable_state || "",
    contract_version: initial?.contract_version || "", document_type: initial?.document_type || "",
    effective_date: (initial?.effective_date || "").slice(0, 10), review_date: (initial?.review_date || "").slice(0, 10),
    expiration_date: (initial?.expiration_date || "").slice(0, 10), link_url: initial?.link_url || "", body_text: "",
    audience: initial?.audience || [], applicable_programs: initial?.applicable_programs || [],
    collection: initial?.collection || "", source_organization: initial?.source_organization || "",
    line_of_business: initial?.line_of_business || "", display_title: initial?.display_title || "",
    bulletin_number: initial?.bulletin_number || "", document_number: initial?.document_number || "",
    issue_date: (initial?.issue_date || "").slice(0, 10),
    applicability_mode: initial?.applicability_mode || "unknown",
    applicable_states: initial?.applicable_states || [], excluded_states: initial?.excluded_states || [],
    document_year: initial?.document_year ? String(initial.document_year) : "", date_kind: initial?.date_kind || "",
    canonical_filename: initial?.canonical_filename || "",
  });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [more, setMore] = useState(isEdit);
  const touched = React.useRef(new Set());
  const lastPublish = React.useRef(false);
  const analyzing = analysis?.status === "processing";
  const detected = analysis?.status === "done" ? analysis : null;
  const review = new Set(detected?.suggestions?.review_fields || []);
  const set = (k, v) => { touched.current.add(k); setForm((f) => ({ ...f, [k]: v })); };

  const analyze = async (f, retryId) => {
    setSaveErr(null); setDup(null);
    try {
      const a = await runKnowledgeAnalysis(f, setAnalysis, retryId);
      if (a.status === "done") setForm((p) => applySuggestions(p, a.suggestions, touched.current));
      if (a.status === "failed") toast.error(a.error || "Analysis failed");
    } catch (e) { setAnalysis({ status: "failed", error: apiError(e) }); }
  };
  const onPickFile = (f) => { setFile(f); setAnalysis(null); if (f) analyze(f); };

  useEffect(() => {
    if (scopes.includes("group") || initial?.organization_id) api.get("/organizations").then(({ data }) => {
      const list = data.organizations || [];
      setOrgs(list);
      const active = list.filter((o) => o.status !== "inactive");
      if (!initial && !scopes.includes("account") && active.length === 1) { setScope("group"); setOrgId(active[0].id); }
    }).catch(() => {});
  }, [initial?.organization_id]); // eslint-disable-line

  const toggleAud = (k) => setForm((f) => ({ ...f, audience: f.audience.includes(k) ? f.audience.filter((x) => x !== k) : [...f.audience, k] }));
  const toggleProg = (k) => setForm((f) => ({ ...f, applicable_programs: f.applicable_programs.includes(k) ? f.applicable_programs.filter((x) => x !== k) : [...f.applicable_programs, k] }));

  const addAsVersion = async (m) => {
    setBusy(true); setSaveErr(null);
    try {
      const fd = new FormData();
      fd.append("analysis_id", analysis.id);
      fd.append("notes", `New edition uploaded${form.issue_date ? ` (${form.issue_date})` : ""}`);
      const { data } = await api.post(`/knowledge/sources/${m.id}/version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Added as version ${data.version} of “${m.title}” — previous versions kept; nothing was published or archived`);
      onSaved();
    } catch (e) { setSaveErr(apiError(e)); } finally { setBusy(false); }
  };

  const save = async (publish, force = false) => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (scope === "group" && !orgId) return toast.error("Choose who this applies to: All Groups / Districts or a specific district");
    if (!isEdit && sourceType === "document" && !file) return toast.error("Choose a file to upload");
    if (!isEdit && sourceType === "document" && !detected) return toast.error("Wait for the document analysis to finish");
    setBusy(true); setDup(null); setSaveErr(null);
    const carrier = form.source_organization || ((meta?.source_organizations || []).find((o) => o.toLowerCase() === (form.publisher || "").toLowerCase()) || "");
    try {
      if (isEdit) {
        await api.patch(`/knowledge/sources/${initial.id}`, {
          ...form, scope_type: scope, organization_id: scope === "group" ? orgId : null,
          source_organization: carrier, document_year: form.document_year ? Number(form.document_year) : null,
          keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        });
        toast.success("Source updated");
        return onSaved();
      }
      if (sourceType === "document") {
        const fd = new FormData();
        fd.append("analysis_id", analysis.id);
        fd.append("scope_type", scope);
        if (scope === "group") fd.append("organization_id", orgId);
        fd.append("status", publish ? "published" : "draft");
        fd.append("force", force ? "true" : "false");
        Object.entries({ title: form.title, description: form.description, category: form.category, topic: form.topic,
          purpose: form.purpose, keywords: form.keywords, publisher: form.publisher,
          document_type: form.document_type, effective_date: form.effective_date,
          issue_date: form.issue_date, review_date: form.review_date, expiration_date: form.expiration_date, audience: form.audience.join(","),
          collection: form.collection, source_organization: carrier, line_of_business: form.line_of_business,
          display_title: form.display_title, bulletin_number: form.bulletin_number, document_number: form.document_number,
          applicability_mode: form.applicability_mode, document_year: form.document_year, date_kind: form.date_kind,
          canonical_filename: form.canonical_filename,
          applicable_states: (form.applicable_states || []).join(","), excluded_states: (form.excluded_states || []).join(","),
          applicable_programs: (form.applicable_programs || []).join(",") })
          .forEach(([k, v]) => { if (v) fd.append(k, v); });
        const { data } = await api.post("/knowledge/sources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.duplicate) { setDup({ ...data.existing, blocked: data.blocked }); setBusy(false); return; }
        toast.success(data.already_saved ? "Already saved — no duplicate created" : (data.display_title ? `Added — “${data.display_title}”` : "Source added"));
      } else {
        await api.post("/knowledge/sources", {
          title: form.title, scope_type: scope, organization_id: scope === "group" ? orgId : null,
          source_type: sourceType, description: form.description, body_text: form.body_text, link_url: form.link_url,
          category: form.category, topic: form.topic, purpose: form.purpose,
          keywords: form.keywords.split(",").map((s) => s.trim()).filter(Boolean), publisher: form.publisher,
          applicable_state: form.applicable_state, document_type: form.document_type,
          effective_date: form.effective_date, issue_date: form.issue_date, review_date: form.review_date, expiration_date: form.expiration_date,
          status: publish ? "published" : "draft", audience: form.audience,
          collection: form.collection, source_organization: form.source_organization, line_of_business: form.line_of_business,
          display_title: form.display_title, bulletin_number: form.bulletin_number, applicability_mode: form.applicability_mode,
          applicable_states: form.applicable_states, excluded_states: form.excluded_states, applicable_programs: form.applicable_programs,
        });
        toast.success("Source added");
      }
      onSaved();
    } catch (e) { setSaveErr(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="knowledge-source-dialog">
        <DialogHeader><DialogTitle>{isEdit ? `Edit — ${initial.display_title || initial.title}` : "Add Knowledge Source"}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto tr-scroll pr-1">
          {dup && (
            <div className={`rounded-lg border p-3 text-sm ${dup.blocked ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid="knowledge-dup-warning">
              <p className={`flex items-center gap-2 font-medium ${dup.blocked ? "text-rose-800" : "text-amber-800"}`}><AlertTriangle className="w-4 h-4" />{dup.blocked ? "Already in the Knowledge Center" : "Possible duplicate source"}</p>
              <p className="mt-1 text-slate-700">Identical file content: <strong>{dup.display_title || dup.title}</strong> ({dup.human_id} · {dup.status}).{dup.blocked ? " It already applies to this audience, so it can't be added again." : ""}</p>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="outline" onClick={() => viewAttachment(fileUrl(dup.id))} data-testid="knowledge-dup-open">Open existing</Button>
                <Button size="sm" variant="outline" onClick={() => setDup(null)} data-testid="knowledge-dup-cancel">Close</Button>
                {!dup.blocked && <Button size="sm" className="bg-[#006090] hover:bg-[#004E75]" onClick={() => save(false, true)} data-testid="knowledge-dup-add">Add for this audience anyway</Button>}
              </div>
            </div>
          )}
          {saveErr && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm" data-testid="knowledge-save-error">
              <p className="font-medium text-rose-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Couldn't save</p>
              <p className="text-rose-700 mt-1">{saveErr}</p>
              <p className="text-xs text-slate-500 mt-1">Your file and entries are kept. Retrying won't create a duplicate.</p>
              <Button size="sm" className="mt-2 bg-[#006090] hover:bg-[#004E75]" onClick={() => save(lastPublish.current)} disabled={busy} data-testid="knowledge-save-retry">Retry</Button>
            </div>
          )}
          {!isEdit && (
            <div className="grid grid-cols-3 gap-2">
              {[["document", "Upload document", FileText], ["text", "Text / Article", Type], ["link", "Link", Link2]].map(([v, l, Ic]) => (
                <button key={v} onClick={() => setSourceType(v)} data-testid={`knowledge-type-${v}`}
                  className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${sourceType === v ? "border-[#006090] bg-[#006090]/5 text-[#006090]" : "border-slate-200 text-slate-600"}`}>
                  <Ic className="w-4 h-4" />{l}
                </button>
              ))}
            </div>
          )}
          {!isEdit && sourceType === "document" && (
            <F label="Document file (PDF, DOCX, TXT, HTML, CSV · max 25 MB; no limit for Super Admins)">
              <Input type="file" accept=".pdf,.doc,.docx,.txt,.html,.md,.csv" onChange={(e) => onPickFile(e.target.files?.[0] || null)} className="mt-1" data-testid="knowledge-file-input" />
            </F>
          )}
          {!isEdit && sourceType === "document" && analysis && (
            <AnalysisPanel analysis={analysis} file={file} onRetry={() => analyze(file, analysis.id)} onReanalyze={() => analyze(file)}
              onAddVersion={addAsVersion} busy={busy} />
          )}
          {!isEdit && sourceType === "text" && (
            <F label="Content"><Textarea value={form.body_text || ""} onChange={(e) => set("body_text", e.target.value)} rows={5} className="mt-1" data-testid="knowledge-body-input" placeholder="Paste the reference text / article…" /></F>
          )}
          {!isEdit && sourceType === "link" && (
            <F label="URL"><Input value={form.link_url} onChange={(e) => set("link_url", e.target.value)} className="mt-1" placeholder="https://…" data-testid="knowledge-link-input" /></F>
          )}

          <div className="grid grid-cols-2 gap-3">
            <RF label="Display Title" k="display_title" review={review} span><Input value={form.display_title} onChange={(e) => set("display_title", e.target.value)} className="mt-1" data-testid="knowledge-input-displaytitle" placeholder="2026 Associate Agent Bonus Program" /></RF>
            <RF label="Title" k="title" review={review} hint="Official title as printed"><Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="knowledge-input-title" /></RF>
            {sourceType === "document" && <RF label="File name" k="canonical_filename" review={review} hint={file || initial?.original_filename ? `Original: ${file?.name || initial?.original_filename}` : null}><Input value={form.canonical_filename} onChange={(e) => set("canonical_filename", e.target.value)} className="mt-1 font-mono text-xs" data-testid="knowledge-input-filename" /></RF>}
            <RF label="Description" k="description" review={review} span><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1" data-testid="knowledge-input-description" /></RF>
            <F label="Applies to" span>
              <AudienceSelect scopes={isEdit && !scopes.includes(scope) ? [...scopes, scope] : scopes} orgs={orgs} scope={scope} orgId={orgId}
                onChange={(sc, o) => { setScope(sc); setOrgId(o); }} />
            </F>
            <div className="col-span-2">
              <AppliesTo scope={scope} accountName={meta?.account_name} orgName={orgs.find((o) => o.id === orgId)?.name} />
              {initial?.scope_issue && <p className="mt-1 text-xs text-amber-700" data-testid="knowledge-scope-issue">{initial.scope_issue}</p>}
            </div>
            <RF label="Category" k="category" review={review}>
              <Select value={form.category || "none"} onValueChange={(v) => set("category", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="knowledge-input-category"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— None —</SelectItem>
                  {(meta?.categories || []).map((c) => <SelectItem key={c.id} value={c.label}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </RF>
            <RF label="Document Type" k="document_type" review={review}>
              <Select value={form.document_type || "none"} onValueChange={(v) => set("document_type", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="knowledge-input-doctype"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">— None —</SelectItem>
                  {(meta?.document_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </RF>
            <RF label="Topic" k="topic" review={review}><Input value={form.topic} onChange={(e) => set("topic", e.target.value)} className="mt-1" data-testid="knowledge-input-topic" /></RF>
            <RF label="Publisher / Source" k="publisher" review={review}><Input list="kc-publishers" value={form.publisher} onChange={(e) => set("publisher", e.target.value)} className="mt-1" data-testid="knowledge-input-publisher" />
              <datalist id="kc-publishers">{(meta?.source_organizations || []).map((o) => <option key={o} value={o} />)}</datalist></RF>
            <RF label="Document / Form Number" k="document_number" review={review} hint="Stored separately, never in the title"><Input value={form.document_number} onChange={(e) => set("document_number", e.target.value)} className="mt-1" data-testid="knowledge-input-docnumber" /></RF>
            <RF label="Document Year" k="document_year" review={review} hint="Only when the document states it"><Input value={form.document_year} onChange={(e) => set("document_year", e.target.value.replace(/\D/g, "").slice(0, 4))} className="mt-1" data-testid="knowledge-input-year" placeholder="YYYY" /></RF>
            <RF label="Issue / Publication Date" k="issue_date" review={review}><Input type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="mt-1" data-testid="knowledge-input-issue" /></RF>
            <F label="Date type">
              <Select value={form.date_kind || "none"} onValueChange={(v) => set("date_kind", v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="knowledge-input-datekind"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">— Not set —</SelectItem><SelectItem value="publication">Publication date</SelectItem><SelectItem value="revision">Revision date</SelectItem></SelectContent>
              </Select>
            </F>
            <RF label="Applicable State(s)" k="applicable_states" review={review}>
              <Select value={form.applicability_mode} onValueChange={(v) => set("applicability_mode", v)}>
                <SelectTrigger className="mt-1" data-testid="knowledge-input-applic-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.applicability_modes || []).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </RF>
          </div>
          {(form.applicability_mode === "selected_states" || form.applicability_mode === "all_except") && (
            <F label={form.applicability_mode === "all_except" ? "Excluded States" : "Applicable States"}
               hint="Applicability is separate from tenant scope — it never broadens access.">
              <div className="mt-1 flex flex-wrap gap-1 max-h-32 overflow-y-auto tr-scroll border border-slate-100 rounded p-2" data-testid="knowledge-state-picker">
                {(meta?.states || []).map((st) => {
                  const key = form.applicability_mode === "all_except" ? "excluded_states" : "applicable_states";
                  const on = (form[key] || []).includes(st.code);
                  return <button key={st.code} type="button" data-testid={`knowledge-state-${st.code}`}
                    onClick={() => { touched.current.add(key); setForm((f) => ({ ...f, [key]: on ? f[key].filter((x) => x !== st.code) : [...(f[key] || []), st.code] })); }}
                    className={`text-[11px] rounded border px-1.5 py-0.5 ${on ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{st.code}</button>;
                })}
              </div>
            </F>
          )}

          <div className="rounded-lg border border-slate-200">
            <button type="button" onClick={() => setMore(!more)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500" data-testid="knowledge-more-toggle">
              Additional Details <span>{more ? "−" : "+"}</span></button>
            {more && <div className="p-3 pt-0 space-y-3" data-testid="knowledge-more">
            <div className="grid grid-cols-2 gap-3">
              <F label="Collection">
                <Select value={form.collection || "none"} onValueChange={(v) => set("collection", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="knowledge-input-collection"><SelectValue placeholder="Collection" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">— None —</SelectItem>
                    {(meta?.collections || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Carrier (if different from Publisher)">
                <Select value={form.source_organization || "none"} onValueChange={(v) => set("source_organization", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="knowledge-input-carrier"><SelectValue placeholder="Same as Publisher" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">Same as Publisher</SelectItem>
                    {(meta?.source_organizations || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Line of Business">
                <Select value={form.line_of_business || "none"} onValueChange={(v) => set("line_of_business", v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1" data-testid="knowledge-input-lob"><SelectValue placeholder="Line of Business" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">— None —</SelectItem>
                    {(meta?.lines_of_business || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              {form.document_type === "Field Bulletin" && <F label="Bulletin Number"><Input value={form.bulletin_number} onChange={(e) => set("bulletin_number", e.target.value)} className="mt-1" data-testid="knowledge-input-bulletin" /></F>}
              <F label="Purpose"><Input value={form.purpose} onChange={(e) => set("purpose", e.target.value)} className="mt-1" data-testid="knowledge-input-purpose" /></F>
            </div>

          <div className="grid grid-cols-3 gap-3">
            <F label="Effective Date"><Input type="date" value={form.effective_date} onChange={(e) => set("effective_date", e.target.value)} className="mt-1" data-testid="knowledge-input-effective" /></F>
            <F label="Review Date"><Input type="date" value={form.review_date} onChange={(e) => set("review_date", e.target.value)} className="mt-1" data-testid="knowledge-input-review" /></F>
            <F label="Expiration Date"><Input type="date" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} className="mt-1" data-testid="knowledge-input-expiration" /></F>
          </div>

          <F label="Applicable Programs" hint="Which candidate programs this guide applies to. Used to recommend guides in the Candidate Portal. Choose All Programs for general guidance.">
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(meta?.programs || []).map((p) => (
                <button key={p.key} type="button" onClick={() => toggleProg(p.key)} data-testid={`knowledge-prog-${p.key}`}
                  className={`text-xs rounded-full border px-2.5 py-1 ${form.applicable_programs.includes(p.key) ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]" : "border-slate-200 text-slate-500"}`}>{p.label}</button>
              ))}
            </div>
          </F>

          <F label="Audience" hint="Who this is intended for (defaults to internal/admin only if none selected)">
            <div className="flex flex-wrap gap-1.5 mt-1">
              {(meta?.audiences || []).map((a) => (
                <button key={a.key} type="button" onClick={() => toggleAud(a.key)} data-testid={`knowledge-aud-${a.key}`}
                  className={`text-xs rounded-full border px-2.5 py-1 ${form.audience.includes(a.key) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{a.label}</button>
              ))}
            </div>
          </F>
            </div>}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!isEdit && <Button variant="outline" onClick={() => { lastPublish.current = false; save(false); }} disabled={busy || analyzing || dup?.blocked} data-testid="knowledge-save-draft">{busy ? <InlineSpinner /> : "Save as Draft"}</Button>}
          <Button onClick={() => { lastPublish.current = true; save(true); }} disabled={busy || analyzing || dup?.blocked} className="bg-[#006090] hover:bg-[#004E75]" data-testid="knowledge-save-publish">{busy ? <InlineSpinner /> : (isEdit ? "Save" : "Publish")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RF({ label, k, review, hint, span, children }) {
  const flag = review?.has(k);
  return (
    <div className={span ? "col-span-2" : ""} data-testid={`knowledge-field-${k}`}>
      <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">{label}{flag && <span className="text-[10px] font-semibold rounded-full bg-amber-100 text-amber-800 px-1.5" data-testid={`knowledge-review-${k}`}>Needs review</span>}</Label>
      <div className={flag ? "rounded-md ring-1 ring-amber-300" : ""}>{children}</div>
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

const MATCH_LABEL = { exact: ["Exact same file", "border-rose-200 bg-rose-50 text-rose-800"], likely_duplicate: ["Likely duplicate", "border-amber-200 bg-amber-50 text-amber-800"],
  revision: ["Possible new revision of", "border-sky-200 bg-sky-50 text-sky-800"] };

function AnalysisPanel({ analysis, onRetry, onReanalyze, onAddVersion, busy }) {
  if (analysis.status === "processing") return (
    <div className="rounded-lg border border-[#006090]/20 bg-[#006090]/5 p-3 text-xs" data-testid="knowledge-analysis-progress">
      <p className="flex items-center gap-2 text-slate-700"><InlineSpinner /> {analysis.step || "Analysing"}… {analysis.progress || 0}%</p>
      <div className="h-1.5 mt-2 rounded bg-slate-200 overflow-hidden"><div className="h-full bg-[#006090] transition-[width] duration-500" style={{ width: `${analysis.progress || 5}%` }} /></div>
      <p className="text-slate-500 mt-1">You can keep editing while the document is read.</p>
    </div>);
  if (analysis.status === "failed") return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs" data-testid="knowledge-analysis-failed">
      <p className="text-rose-800 font-medium">Couldn't read this document: {analysis.error}</p>
      <Button size="sm" variant="outline" className="mt-2" onClick={analysis.id ? onRetry : onReanalyze} data-testid="knowledge-analysis-retry">Retry analysis</Button>
    </div>);
  const s = analysis.suggestions || {};
  return (
    <div className="space-y-2" data-testid="knowledge-detected-panel">
      <div className="rounded-lg border border-[#006090]/20 bg-[#006090]/5 p-3 text-xs flex items-center justify-between gap-2">
        <span className="text-slate-700"><CheckCircle2 className="inline w-3.5 h-3.5 text-emerald-600 mr-1" />Details filled from the document{analysis.ocr_used ? " (scanned pages read)" : ""}. {s.review_fields?.length ? <b className="text-amber-700">{s.review_fields.length} field(s) need review.</b> : "Check each field before saving."}{analysis.unreadable ? " Text could not be read." : ""}</span>
        <Button size="sm" variant="ghost" onClick={onReanalyze} className="h-7 text-xs" data-testid="knowledge-reanalyze"><RefreshCw className="w-3 h-3 mr-1" />Re-analyse</Button>
      </div>
      {analysis.confidential_warning && <p className="text-xs text-amber-700" data-testid="knowledge-confidential">Confidential / proprietary wording found — it will be kept internal and flagged for review.</p>}
      {(analysis.matches || []).map((m) => (
        <div key={m.id} className={`rounded-lg border p-3 text-xs ${MATCH_LABEL[m.kind][1]}`} data-testid={`knowledge-match-${m.kind}`}>
          <p className="font-semibold">{MATCH_LABEL[m.kind][0]}: {m.title}</p>
          <p className="mt-0.5 text-slate-600">{m.human_id || ""} · {m.date || "no date"} · {m.status} · {m.scope_label}{m.version ? ` · v${m.version}` : ""}</p>
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => viewAttachment(fileUrl(m.id))} data-testid={`knowledge-match-open-${m.id}`}>Open</Button>
            {m.kind !== "exact" && m.can_manage && <Button size="sm" className="h-7 text-xs bg-[#006090] hover:bg-[#004E75]" disabled={busy} onClick={() => onAddVersion(m)} data-testid={`knowledge-match-version-${m.id}`}>Add as new version</Button>}
          </div>
        </div>))}
    </div>
  );
}

function VersionDialog({ source, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [eff, setEff] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!file) return toast.error("Choose a file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (notes) fd.append("notes", notes);
      if (eff) fd.append("effective_date", eff);
      await api.post(`/knowledge/sources/${source.id}/version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New version uploaded"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="knowledge-version-dialog">
        <DialogHeader><DialogTitle>Upload new version — {source.display_title || source.title}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Current version v{source.version} will be retained in history (superseded).</p>
        <div className="space-y-3">
          <F label="New file"><Input type="file" accept=".pdf,.doc,.docx,.txt,.html,.md,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1" data-testid="knowledge-version-file" /></F>
          <F label="Effective Date"><Input type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="mt-1" data-testid="knowledge-version-effective" /></F>
          <F label="Notes"><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" placeholder="What changed in this version?" data-testid="knowledge-version-notes" /></F>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#006090] hover:bg-[#004E75]" data-testid="knowledge-version-save">{busy ? <InlineSpinner /> : "Upload version"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Collections({ meta, canManage, onChanged }) {
  const [cols, setCols] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/knowledge/collections").then(({ data }) => setCols(data.collections)).catch(() => {}); }, [meta]);
  const add = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try { await api.post("/knowledge/categories", { label: label.trim() }); setLabel(""); toast.success("Topic added"); onChanged(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <p className="text-sm text-slate-500 mb-4">Collections are the stable business areas for browsing. Counts reflect sources you can access.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="knowledge-collections-grid">
        {(cols || []).map((c) => (
          <div key={c.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`knowledge-collection-${c.key}`}>
            <p className="font-semibold text-slate-800 text-sm">{c.label}</p>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
            <div className="flex gap-3 mt-3 text-xs">
              <span className="text-emerald-700 font-medium">{c.published} published</span>
              <span className="text-slate-400">{c.total} total</span>
              {c.needs_review > 0 && <span className="text-amber-600">{c.needs_review} review</span>}
            </div>
          </div>
        ))}
      </div>
      {canManage && (
        <div className="mt-6 max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Topics (within collections)</p>
          <div className="flex gap-2 mb-3">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="New topic name" data-testid="knowledge-cat-input" onKeyDown={(e) => e.key === "Enter" && add()} />
            <Button onClick={add} disabled={busy} className="bg-[#006090] hover:bg-[#004E75]" data-testid="knowledge-cat-add">{busy ? <InlineSpinner /> : "Add"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}


const REASON_CLS = {
  "Conflicting Jurisdiction Evidence": "border-rose-200 text-rose-700 bg-rose-50",
  "Missing State Applicability": "border-amber-200 text-amber-700 bg-amber-50",
  "Text Extraction Failed": "border-rose-200 text-rose-700 bg-rose-50",
  "Missing Collection": "border-slate-200 text-slate-600 bg-slate-50",
  "Unverified Document Year": "border-amber-200 text-amber-700 bg-amber-50",
  "Review Due": "border-sky-200 text-sky-700 bg-sky-50",
  "Expired": "border-rose-200 text-rose-700 bg-rose-50",
};

function ReviewQueue({ items, onEdit }) {
  return (
    <div className="space-y-2" data-testid="knowledge-review-list">
      {items.map((s) => (
        <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3" data-testid={`knowledge-review-${s.id}`}>
          <div className="min-w-0">
            <p className="font-medium text-slate-800 truncate">{s.display_title || s.title}</p>
            <p className="text-xs text-slate-400 mt-0.5">{s.human_id}{s.original_filename ? ` · ${s.original_filename}` : ""}</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(s.review_reasons || []).map((r) => (
                <Badge key={r} variant="outline" className={`text-[11px] ${REASON_CLS[r] || "border-slate-200 text-slate-600"}`} data-testid={`review-reason-${r.replace(/ /g, "-")}`}>{r}</Badge>
              ))}
            </div>
            {s.applicability_evidence?.items?.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                Detected: <span className="font-medium">{(s.applicable_states || []).join(", ") || s.applicability_mode}</span>
                {" — "}{s.applicability_evidence.items.map((e) => `“${e.text}”${e.page ? ` (p.${e.page})` : ""}`).join("; ")}
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => onEdit(s)} data-testid={`review-resolve-${s.id}`}>Review & Fix</Button>
        </div>
      ))}
    </div>
  );
}

const BULK_STATUS_ICON = { analyzing: "text-sky-500", ready: "text-slate-300", saving: "text-sky-500",
  done: "text-emerald-500", review: "text-amber-500", duplicate: "text-amber-500", error: "text-rose-500" };

function BulkUploadDialog({ meta, onClose, onDone }) {
  const scopes = meta?.manageable_scopes || ["account"];
  const [defScope, setDefScope] = useState(defaultScope(scopes));
  const [orgId, setOrgId] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [rows, setRows] = useState([]);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [ba, setBa] = useState({ collection: "", category: "", scope: "", applicability_mode: "", audience: [] });

  useEffect(() => {
    if (scopes.includes("group")) api.get("/organizations").then(({ data }) => setOrgs(data.organizations || [])).catch(() => {});
  }, []); // eslint-disable-line

  const analyze = async (row) => {
    try {
      const data = await runKnowledgeAnalysis(row.file, (a) => setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, progress: a.progress, step: a.step } : r)));
      if (data.status !== "done") throw new Error(data.error || "Analysis failed");
      const s = data.suggestions || {};
      const mt = (data.matches || [])[0];
      const relTxt = mt ? `${mt.kind === "exact" ? "Exact duplicate of" : mt.kind === "revision" ? "Possible new revision of" : "Likely duplicate of"} "${mt.title}" (${mt.scope_label})` : "New source";
      const warn = [s.review_fields?.length ? `Needs review: ${s.review_fields.join(", ")}` : null, data.ocr_used ? "Scanned pages read" : null,
        data.confidential_warning ? "Confidential wording detected" : null, relTxt].filter(Boolean).join("; ");
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, status: mt?.kind === "exact" ? "duplicate" : "ready", analysisId: data.id, warn,
        detected: { ...data, confidence: s.review_fields?.length ? "medium" : "high", document_year: s.document_year, suggested_type: s.document_type },
        needsReview: (s.review_fields || []).length > 0 || !!mt || data.confidential_warning,
        edit: { display_title: s.display_title || "", document_year: s.document_year || "", title: s.title || "", description: s.description || "",
          document_number: s.document_number || "", document_type: s.document_type || "", collection: "", category: s.category || "", topic: s.topic || "",
          publisher: s.publisher || "", canonical_filename: s.file_name || "", date_kind: s.date_kind || "",
          scope: defScope, applicability_mode: s.applicability_mode || "unknown", applicable_states: s.applicable_states || [],
          audience: data.confidential_warning ? ["internal_recruiting"] : [], effective_date: s.effective_date || "", issue_date: s.issue_date || "",
          source_authority: "", applicable_programs: [], content_key: "", folder_path: row.file.webkitRelativePath || "" } } : r));
    } catch (e) {
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, status: "error", warn: apiError(e) || e.message } : r));
    }
  };
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList).map((f) => ({ file: f, id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 6)}`, status: "analyzing", selected: true, detected: null, edit: {} }));
    setRows((r) => [...r, ...incoming]);
    incoming.forEach(analyze);
  };
  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); };
  const setEdit = (id, k, v) => setRows((rs) => rs.map((r) => r.id === id ? { ...r, edit: { ...r.edit, [k]: v } } : r));
  const applyYearToTitle = (title, y) => { const base = (title || "").replace(/^(?:19|20)\d{2}\s+/, "").trim(); return y ? `${y} ${base}`.trim() : base; };
  const setYear = (id, y) => setRows((rs) => rs.map((r) => r.id === id ? { ...r, edit: { ...r.edit, document_year: y, display_title: applyYearToTitle(r.edit.display_title, y) } } : r));
  const toggleSel = (id) => setRows((rs) => rs.map((r) => r.id === id ? { ...r, selected: !r.selected } : r));
  const allSel = rows.length > 0 && rows.every((r) => r.selected);
  const toggleAll = () => setRows((rs) => rs.map((r) => ({ ...r, selected: !allSel })));
  const selectedReady = rows.filter((r) => r.selected && r.status === "ready");

  const applyBulk = () => {
    if (!ba.collection && !ba.scope && !ba.applicability_mode && !ba.audience.length) return toast.error("Choose a value to apply");
    setRows((rs) => rs.map((r) => (r.selected && r.status === "ready") ? { ...r, edit: { ...r.edit,
      ...(ba.collection ? { collection: ba.collection } : {}),
      ...(ba.category ? { category: ba.category } : {}),
      ...(ba.scope ? { scope: ba.scope } : {}),
      ...(ba.applicability_mode ? { applicability_mode: ba.applicability_mode, applicable_states: [] } : {}),
      ...(ba.audience.length ? { audience: ba.audience } : {}) } } : r));
    toast.success(`Applied to ${selectedReady.length} selected`);
  };

  const saveRows = async (list, review) => {
    if (!list.length) return toast.error("No files selected");
    if (list.some((r) => (r.edit.scope || defScope) === "group") && !orgId) return toast.error("Select a Group / District for Group scope");
    setBusy(true);
    for (const row of list) {
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, status: "saving" } : r));
      try {
        const e = row.edit;
        const fd = new FormData();
        fd.append("analysis_id", row.analysisId);
        fd.append("scope_type", e.scope || defScope);
        if ((e.scope || defScope) === "group") fd.append("organization_id", orgId);
        fd.append("status", "draft");
        if (review || row.needsReview) fd.append("force_review", "true");
        const map = { display_title: e.display_title, document_year: e.document_year, document_number: e.document_number,
          document_type: e.document_type, collection: e.collection, category: e.category, topic: e.topic, title: e.title || e.display_title,
          description: e.description, publisher: e.publisher, canonical_filename: e.canonical_filename, date_kind: e.date_kind,
          applicability_mode: e.applicability_mode, effective_date: e.effective_date, issue_date: e.issue_date,
          source_authority: e.source_authority, content_key: e.content_key, folder_path: e.folder_path };
        Object.entries(map).forEach(([k, v]) => { if (v) fd.append(k, v); });
        if (e.applicable_programs?.length) fd.append("applicable_programs", e.applicable_programs.join(","));
        if (e.applicable_states?.length) fd.append("applicable_states", e.applicable_states.join(","));
        if (e.audience?.length) fd.append("audience", e.audience.join(","));
        const { data } = await api.post("/knowledge/sources/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, status: data.duplicate ? "duplicate" : (review ? "review" : "done"), selected: false } : r));
      } catch (err) {
        setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, status: "error", warn: apiError(err) } : r));
      }
    }
    setBusy(false);
    onDone();
    toast.success(review ? "Saved selected to Review Queue (Draft)" : "Saved selected as Draft");
  };
  const acceptHighConf = () => {
    const hi = rows.filter((r) => r.status === "ready" && r.detected?.confidence === "high");
    if (!hi.length) return toast.error("No high-confidence files to accept");
    saveRows(hi, false);
  };
  const removeSelected = () => setRows((rs) => rs.filter((r) => !(r.selected && r.status !== "saving")));
  const toggleAud = (id, k) => setRows((rs) => rs.map((r) => r.id === id ? { ...r, edit: { ...r.edit, audience: r.edit.audience?.includes(k) ? r.edit.audience.filter((x) => x !== k) : [...(r.edit.audience || []), k] } } : r));
  const toggleState = (id, c) => setRows((rs) => rs.map((r) => r.id === id ? { ...r, edit: { ...r.edit, applicable_states: r.edit.applicable_states?.includes(c) ? r.edit.applicable_states.filter((x) => x !== c) : [...(r.edit.applicable_states || []), c] } } : r));
  const baAud = (k) => setBa((b) => ({ ...b, audience: b.audience.includes(k) ? b.audience.filter((x) => x !== k) : [...b.audience, k] }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-5xl" data-testid="knowledge-bulk-dialog">
        <DialogHeader><DialogTitle>Bulk Upload — Upload Inbox</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Files are read and identified from their content first. Review &amp; correct the suggestions below, then save as Draft. Nothing is published automatically.</p>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <F label="Applies to (new files)">
            <AudienceSelect scopes={scopes} orgs={orgs} scope={defScope} orgId={orgId} onChange={(sc, o) => { setDefScope(sc); setOrgId(o); setRows((rs) => rs.map((r) => (r.edit && r.status !== "saved" ? { ...r, edit: { ...r.edit, scope: sc } } : r))); }} testId="bulk-scope" />
          </F>
          {rows.some((r) => r.edit?.scope === "group") && defScope !== "group" && (
            <F label="District for files set to a single district">
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger className="mt-1" data-testid="bulk-org"><SelectValue placeholder="Select Group / District" /></SelectTrigger>
                <SelectContent className="bg-white">{orgs.filter((o) => o.status !== "inactive").map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          )}
          <div className="col-span-2"><AppliesTo scope={defScope} accountName={meta?.account_name} orgName={orgs.find((o) => o.id === orgId)?.name} testId="bulk-applies-to" /></div>
        </div>
        <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}
          className={`mt-3 flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-5 cursor-pointer text-center ${drag ? "border-[#006090] bg-[#006090]/5" : "border-slate-200"}`} data-testid="bulk-dropzone">
          <Upload className="w-6 h-6 text-slate-400 mb-1.5" />
          <span className="text-sm text-slate-600">Drag &amp; drop files here, or click to choose</span>
          <span className="text-[11px] text-slate-400 mt-0.5">PDF, DOCX, TXT, HTML, CSV · max 25 MB each (no limit for Super Admins)</span>
          <input type="file" multiple accept=".pdf,.doc,.docx,.txt,.html,.md,.csv" className="hidden" onChange={(e) => e.target.files && addFiles(e.target.files)} data-testid="bulk-file-input" />
        </label>

        {selectedReady.length > 0 && (
          <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg border border-[#006090]/25 bg-[#006090]/5 p-2.5" data-testid="bulk-apply-bar">
            <span className="text-xs font-semibold text-[#006090] mb-1.5">Apply to {selectedReady.length} selected:</span>
            <MiniSelect label="Collection" value={ba.collection} onChange={(v) => setBa((b) => ({ ...b, collection: v }))} options={(meta?.collections || []).map((c) => [c.key, c.label])} testId="ba-collection" />
            <MiniSelect label="Category" value={ba.category} onChange={(v) => setBa((b) => ({ ...b, category: v }))} options={(meta?.categories || []).map((c) => [c.label, c.label])} testId="ba-category" />
            <MiniSelect label="Scope" value={ba.scope} onChange={(v) => setBa((b) => ({ ...b, scope: v }))} options={scopes.map((s) => [s, SCOPE_META[s]?.label || s])} testId="ba-scope" />
            <MiniSelect label="Applicability" value={ba.applicability_mode} onChange={(v) => setBa((b) => ({ ...b, applicability_mode: v }))} options={(meta?.applicability_modes || []).filter((m) => m.key !== "unknown").map((m) => [m.key, m.label])} testId="ba-applic" />
            <div className="flex flex-col"><span className="text-[10px] text-slate-400 mb-0.5">Audience</span>
              <div className="flex flex-wrap gap-1 max-w-[220px]">{(meta?.audiences || []).slice(0, 6).map((a) => (
                <button key={a.key} type="button" onClick={() => baAud(a.key)} className={`text-[10px] rounded-full border px-1.5 py-0.5 ${ba.audience.includes(a.key) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{a.label}</button>))}</div>
            </div>
            <Button size="sm" className="bg-[#006090] hover:bg-[#004E75] ml-auto" onClick={applyBulk} data-testid="bulk-apply-btn">Apply</Button>
          </div>
        )}

        {rows.length > 0 && (
          <div className="mt-3 max-h-[42vh] overflow-auto tr-scroll rounded-lg border border-slate-100" data-testid="bulk-file-list">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50 sticky top-0">
                <th className="px-2 py-1.5"><button onClick={toggleAll} data-testid="bulk-select-all">{allSel ? <CheckSquare className="w-4 h-4 text-[#006090]" /> : <Square className="w-4 h-4" />}</button></th>
                {["File", "Display Title", "Year", "Type", "Conf.", "Status", ""].map((h) => <th key={h} className="px-2 py-1.5 whitespace-nowrap font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((x) => (
                  <React.Fragment key={x.id}>
                    <tr className={`border-b border-slate-50 ${x.selected ? "bg-[#006090]/5" : ""}`} data-testid={`bulk-row-${x.id}`}>
                      <td className="px-2 py-1.5 align-top"><button onClick={() => toggleSel(x.id)} data-testid={`bulk-select-${x.id}`}>{x.selected ? <CheckSquare className="w-4 h-4 text-[#006090]" /> : <Square className="w-4 h-4 text-slate-300" />}</button></td>
                      <td className="px-2 py-1.5 align-top max-w-[150px]">
                        <div className="truncate text-slate-500" title={x.file.name}>{x.file.name}</div>
                        {x.warn && x.detected?.confidence !== "high" ? <div className="text-amber-600 text-[10px] mt-0.5">{x.warn}</div> : null}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {x.status === "ready" ? <Input value={x.edit.display_title} onChange={(e) => setEdit(x.id, "display_title", e.target.value)} className="h-7 text-xs min-w-[180px]" data-testid={`bulk-edit-title-${x.id}`} />
                          : <span className="text-slate-600">{x.edit?.display_title || x.detected?.suggestions?.display_title || "—"}</span>}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {x.status === "ready" ? <Input value={x.edit.document_year} onChange={(e) => setYear(x.id, e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} placeholder="—" className="h-7 text-xs w-16" data-testid={`bulk-edit-year-${x.id}`} />
                          : <span className="tabular-nums">{x.detected?.document_year || "—"}</span>}
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {x.status === "ready" ? (
                          <Select value={x.edit.document_type || "none"} onValueChange={(v) => setEdit(x.id, "document_type", v === "none" ? "" : v)}>
                            <SelectTrigger className="h-7 text-xs min-w-[130px]" data-testid={`bulk-edit-type-${x.id}`}><SelectValue placeholder="Type" /></SelectTrigger>
                            <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{(meta?.document_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <span>{x.detected?.suggested_type || "—"}</span>}
                      </td>
                      <td className="px-2 py-1.5 align-top">{x.detected?.confidence ? <ConfidenceBadge level={x.detected.confidence} ai={x.detected.ai_identified} /> : (x.status === "analyzing" ? <InlineSpinner /> : "—")}</td>
                      <td className="px-2 py-1.5 align-top"><span className={`capitalize ${BULK_STATUS_ICON[x.status] || "text-slate-500"}`}>{x.status}</span></td>
                      <td className="px-2 py-1.5 align-top">{x.status === "ready" && <button onClick={() => setExpanded(expanded === x.id ? null : x.id)} className="text-slate-400 hover:text-[#006090]" data-testid={`bulk-expand-${x.id}`}>{expanded === x.id ? "▲" : "▼"}</button>}</td>
                    </tr>
                    {expanded === x.id && x.status === "ready" && (
                      <tr className="bg-slate-50/60" data-testid={`bulk-expand-panel-${x.id}`}>
                        <td colSpan={8} className="px-3 py-3">
                          <div className="grid grid-cols-3 gap-3">
                            <MiniField label="Document Number"><Input value={x.edit.document_number} onChange={(e) => setEdit(x.id, "document_number", e.target.value)} className="h-7 text-xs" data-testid={`bulk-edit-num-${x.id}`} /></MiniField>
                            <MiniField label="Topic / Program"><Input value={x.edit.topic} onChange={(e) => setEdit(x.id, "topic", e.target.value)} className="h-7 text-xs" data-testid={`bulk-edit-topic-${x.id}`} /></MiniField>
                            <MiniField label="Scope">
                              <Select value={x.edit.scope} onValueChange={(v) => setEdit(x.id, "scope", v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`bulk-edit-scope-${x.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-white">{scopes.map((s) => <SelectItem key={s} value={s}>{s === "group" ? "Single district" : SCOPE_META[s]?.label || s}</SelectItem>)}</SelectContent>
                              </Select>
                            </MiniField>
                            <MiniField label="Collection">
                              <Select value={x.edit.collection || "none"} onValueChange={(v) => setEdit(x.id, "collection", v === "none" ? "" : v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`bulk-edit-collection-${x.id}`}><SelectValue placeholder="Collection" /></SelectTrigger>
                                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{(meta?.collections || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </MiniField>
                            <MiniField label="Category">
                              <Select value={x.edit.category || "none"} onValueChange={(v) => setEdit(x.id, "category", v === "none" ? "" : v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`bulk-edit-category-${x.id}`}><SelectValue placeholder="Category" /></SelectTrigger>
                                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{(meta?.categories || []).map((c) => <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </MiniField>
                            <MiniField label="Effective Date"><Input type="date" value={x.edit.effective_date} onChange={(e) => setEdit(x.id, "effective_date", e.target.value)} className="h-7 text-xs" data-testid={`bulk-edit-eff-${x.id}`} /></MiniField>
                            <MiniField label="Issue Date"><Input type="date" value={x.edit.issue_date} onChange={(e) => setEdit(x.id, "issue_date", e.target.value)} className="h-7 text-xs" data-testid={`bulk-edit-issue-${x.id}`} /></MiniField>
                            <MiniField label="Applicability">
                              <Select value={x.edit.applicability_mode} onValueChange={(v) => setEdit(x.id, "applicability_mode", v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`bulk-edit-applic-${x.id}`}><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-white">{(meta?.applicability_modes || []).map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </MiniField>
                            {(x.edit.applicability_mode === "selected_states" || x.edit.applicability_mode === "all_except") && (
                              <MiniField label={x.edit.applicability_mode === "all_except" ? "Excluded States" : "Applicable States"}>
                                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto tr-scroll">{(meta?.states || []).map((st) => (
                                  <button key={st.code} type="button" onClick={() => toggleState(x.id, st.code)} className={`text-[10px] rounded border px-1 py-0.5 ${x.edit.applicable_states?.includes(st.code) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{st.code}</button>))}</div>
                              </MiniField>
                            )}
                            <MiniField label="Applicable Programs" wide>
                              <div className="flex flex-wrap gap-1">{(meta?.programs || []).map((p) => (
                                <button key={p.key} type="button" onClick={() => setEdit(x.id, "applicable_programs", x.edit.applicable_programs?.includes(p.key) ? x.edit.applicable_programs.filter((v) => v !== p.key) : [...(x.edit.applicable_programs || []), p.key])} data-testid={`bulk-prog-${x.id}-${p.key}`} className={`text-[10px] rounded-full border px-1.5 py-0.5 ${x.edit.applicable_programs?.includes(p.key) ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]" : "border-slate-200 text-slate-500"}`}>{p.label}</button>))}</div>
                            </MiniField>
                            <MiniField label="Audience" wide>
                              <div className="flex flex-wrap gap-1">{(meta?.audiences || []).map((a) => (
                                <button key={a.key} type="button" onClick={() => toggleAud(x.id, a.key)} className={`text-[10px] rounded-full border px-1.5 py-0.5 ${x.edit.audience?.includes(a.key) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{a.label}</button>))}</div>
                            </MiniField>
                          </div>
                          {x.detected?.suggested_canonical_filename && <p className="text-[10px] text-slate-400 mt-2 font-mono">Suggested filename: {x.detected.suggested_canonical_filename}</p>}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="gap-2 mt-2 flex-wrap">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={removeSelected} disabled={!rows.some((r) => r.selected)} data-testid="bulk-remove-selected"><Trash2 className="w-3.5 h-3.5 mr-1" />Remove Selected</Button>
          <Button variant="outline" onClick={acceptHighConf} disabled={busy} data-testid="bulk-accept-high"><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Accept All High Confidence</Button>
          <Button variant="outline" onClick={() => saveRows(selectedReady, true)} disabled={busy || !selectedReady.length} data-testid="bulk-send-review"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Send Selected to Review</Button>
          <Button className="bg-[#006090] hover:bg-[#004E75]" onClick={() => saveRows(selectedReady, false)} disabled={busy || !selectedReady.length} data-testid="bulk-save-draft">{busy ? <InlineSpinner /> : `Save ${selectedReady.length || ""} as Draft`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MiniField = ({ label, children, wide }) => (
  <div className={wide ? "col-span-3" : ""}><span className="text-[10px] text-slate-400 block mb-0.5">{label}</span>{children}</div>
);
function MiniSelect({ label, value, onChange, options, testId }) {
  return (
    <div className="flex flex-col"><span className="text-[10px] text-slate-400 mb-0.5">{label}</span>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger className="h-7 text-xs min-w-[130px]" data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}


function ReplaceFileDialog({ source, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Choose a replacement file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (reason) fd.append("reason", reason);
      await api.post(`/knowledge/sources/${source.id}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced — version and metadata unchanged"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="knowledge-replace-dialog">
        <DialogHeader><DialogTitle>Replace file</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
          <p className="font-medium text-slate-700 mb-1">{source.display_title || source.title} · v{source.version}</p>
          Use this for a better/corrected physical file of the <b>same</b> version (clearer scan, fixed OCR, wrong file). The Source, version, metadata and effective date stay unchanged; the previous file is kept in file history. For a substantive content change, use <b>Upload new version</b> instead.
        </div>
        <div className="mt-3 space-y-3">
          <div className="text-xs text-slate-500">Current file: <span className="font-mono">{source.original_filename || "—"}</span></div>
          <Input type="file" accept=".pdf,.doc,.docx,.txt,.html,.md,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="replace-file-input" />
          <F label="Replacement reason (optional)"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="e.g. Higher-quality scan" data-testid="replace-reason" /></F>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#006090] hover:bg-[#004E75]" data-testid="replace-confirm">{busy ? <InlineSpinner /> : "Replace File"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileHistoryDialog({ source, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/knowledge/sources/${source.id}`).then(({ data }) => setData(data)).catch(() => {}); }, [source]);
  const hist = data?.file_history || [];
  const versions = data?.versions || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="knowledge-history-dialog">
        <DialogHeader><DialogTitle>File History — {source.display_title || source.title}</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Current file</p>
            <p className="font-mono text-slate-700">{data?.original_filename || "—"}</p>
            <p className="text-[11px] text-slate-400">Canonical: {data?.canonical_filename || "—"} · v{data?.version}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Versions</p>
            {versions.length === 0 ? <p className="text-slate-400 text-xs">—</p> : versions.map((v) => (
              <div key={v.id} className="flex justify-between border-b border-slate-100 py-1">
                <span>v{v.version} · <span className="font-mono text-xs">{v.original_filename}</span></span>
                <span className="text-xs text-slate-400">{v.status}{v.effective_date ? ` · eff ${v.effective_date.slice(0, 10)}` : ""}</span>
              </div>
            ))}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Replaced files</p>
            {hist.length === 0 ? <p className="text-slate-400 text-xs">No file replacements.</p> : hist.map((h, i) => (
              <div key={i} className="border-b border-slate-100 py-1.5" data-testid={`history-replaced-${i}`}>
                <p className="font-mono text-xs text-slate-700">{h.original_filename}</p>
                <p className="text-[11px] text-slate-400">Replaced {(h.replaced_at || "").slice(0, 16).replace("T", " ")}{h.reason ? ` · ${h.reason}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const BULK_TITLES = {
  archive: "Archive selected sources", restore: "Restore selected sources", delete: "Delete selected sources",
  collection: "Change Collection", category: "Change Category", applicability: "Change Applicability", audience: "Change Audience",
};

function BulkDialog({ mode, items, meta, isSuper, onClose, onDone }) {
  const ids = items.map((i) => i.id);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [collection, setCollection] = useState("");
  const category = collection; // reuse the same select state for the category mode
  const [applicMode, setApplicMode] = useState("all_states");
  const [states, setStates] = useState([]);
  const [audience, setAudience] = useState([]);
  const [force, setForce] = useState(false);

  useEffect(() => {
    if (mode === "delete") api.post("/knowledge/bulk/delete", { ids, dry_run: true }).then(({ data }) => setPreview(data)).catch((e) => toast.error(apiError(e)));
  }, [mode]); // eslint-disable-line

  const finish = (msg) => { toast.success(msg); onDone(); };
  const post = async (path, payload, msg) => {
    setBusy(true);
    try { const { data } = await api.post(path, payload); finish(typeof msg === "function" ? msg(data) : msg); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  const archivable = items.filter((i) => i.status !== "archived").length;
  const restorable = items.filter((i) => i.status === "archived").length;
  const toggleState = (c) => setStates((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);
  const toggleAud = (k) => setAudience((a) => a.includes(k) ? a.filter((x) => x !== k) : [...a, k]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="knowledge-bulk-action-dialog">
        <DialogHeader><DialogTitle>{BULK_TITLES[mode]} · {items.length}</DialogTitle></DialogHeader>

        {mode === "archive" && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Archive <strong>{archivable}</strong> source{archivable === 1 ? "" : "s"}? They stay in <em>Archived</em> and can be restored later. Files, versions, history and audit are preserved; they are removed from normal results and AI retrieval.</p>
            <F label="Reason (optional)"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="bulk-archive-reason" /></F>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy || archivable === 0} data-testid="bulk-archive-confirm"
                onClick={() => post("/knowledge/bulk/archive", { ids, reason }, (d) => `Archived ${d.archived} source${d.archived === 1 ? "" : "s"}`)}>{busy ? <InlineSpinner /> : `Archive ${archivable}`}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "restore" && (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">Restore <strong>{restorable}</strong> archived source{restorable === 1 ? "" : "s"}. Sources with a competing Current version will be restored as <em>Draft</em> and flagged for review — never silently made Current.</p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy || restorable === 0} data-testid="bulk-restore-confirm"
                onClick={() => post("/knowledge/bulk/restore", { ids }, (d) => `Restored ${d.restored}${d.needs_review?.length ? ` · ${d.needs_review.length} need review` : ""}`)}>{busy ? <InlineSpinner /> : `Restore ${restorable}`}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "delete" && (
          <div className="space-y-3 text-sm">
            {!preview ? <div className="py-6 flex justify-center"><InlineSpinner /></div> : (
              <>
                <p className="text-slate-600"><strong>{preview.eligible.length}</strong> eligible for permanent deletion · <strong>{preview.blocked.length}</strong> must be archived instead (Published/Superseded/history).</p>
                {preview.blocked.length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 max-h-32 overflow-auto tr-scroll text-xs" data-testid="bulk-delete-blocked">
                    {preview.blocked.map((b) => <p key={b.id} className="text-amber-800 truncate">• {b.title} — {b.reasons.join(", ")}</p>)}
                  </div>
                )}
                <F label="Reason"><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" placeholder="Why are these being deleted?" data-testid="bulk-delete-reason" /></F>
                {isSuper && preview.blocked.length > 0 && (
                  <label className="flex items-center gap-2 text-xs text-rose-600" data-testid="bulk-delete-force">
                    <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                    Force-delete blocked records too (exceptional — Super Admin, requires reason)
                  </label>
                )}
                <DialogFooter className="gap-2 flex-wrap">
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  {preview.blocked.length > 0 && (
                    <Button variant="outline" disabled={busy} data-testid="bulk-archive-remaining"
                      onClick={() => post("/knowledge/bulk/archive", { ids: preview.blocked.map((b) => b.id), reason }, (d) => `Archived remaining ${d.archived}`)}>Archive remaining {preview.blocked.length}</Button>
                  )}
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || (!force && preview.eligible.length === 0)} data-testid="bulk-delete-confirm"
                    onClick={() => post("/knowledge/bulk/delete", { ids, reason, force }, (d) => `Permanently deleted ${d.deleted}`)}>{busy ? <InlineSpinner /> : (force ? `Force delete ${items.length}` : `Delete eligible ${preview.eligible.length}`)}</Button>
                </DialogFooter>
              </>
            )}
          </div>
        )}

        {mode === "collection" && (
          <div className="space-y-3 text-sm">
            <F label="Move to Collection">
              <Select value={collection} onValueChange={setCollection}>
                <SelectTrigger className="mt-1" data-testid="bulk-collection-select"><SelectValue placeholder="Select a collection" /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.collections || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy || !collection} data-testid="bulk-collection-confirm"
                onClick={() => post("/knowledge/bulk/metadata", { ids, changes: { collection } }, (d) => `Updated ${d.updated}`)}>{busy ? <InlineSpinner /> : "Apply"}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "category" && (
          <div className="space-y-3 text-sm">
            <F label="Set Category">
              <Select value={category} onValueChange={setCollection} data-testid="bulk-category-select-wrap">
                <SelectTrigger className="mt-1" data-testid="bulk-category-select"><SelectValue placeholder="Select a category" /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.categories || []).map((c) => <SelectItem key={c.id || c.label} value={c.label}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy || !category} data-testid="bulk-category-confirm"
                onClick={() => post("/knowledge/bulk/metadata", { ids, changes: { category } }, (d) => `Updated ${d.updated}`)}>{busy ? <InlineSpinner /> : "Apply"}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "applicability" && (
          <div className="space-y-3 text-sm">
            <F label="Applicability">
              <Select value={applicMode} onValueChange={setApplicMode}>
                <SelectTrigger className="mt-1" data-testid="bulk-applic-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(meta?.applicability_modes || []).filter((m) => m.key !== "unknown").map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {(applicMode === "selected_states" || applicMode === "all_except") && (
              <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto tr-scroll border border-slate-100 rounded p-2" data-testid="bulk-state-picker">
                {(meta?.states || []).map((st) => (
                  <button key={st.code} type="button" onClick={() => toggleState(st.code)}
                    className={`text-[11px] rounded border px-1.5 py-0.5 ${states.includes(st.code) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{st.code}</button>
                ))}
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy} data-testid="bulk-applic-confirm"
                onClick={() => post("/knowledge/bulk/metadata", { ids, changes: { applicability_mode: applicMode, applicable_states: applicMode === "selected_states" ? states : [], excluded_states: applicMode === "all_except" ? states : [] } }, (d) => `Updated ${d.updated}`)}>{busy ? <InlineSpinner /> : "Apply"}</Button>
            </DialogFooter>
          </div>
        )}

        {mode === "audience" && (
          <div className="space-y-3 text-sm">
            <F label="Set Audience (replaces current audience on all selected)">
              <div className="flex flex-wrap gap-1.5 mt-1">
                {(meta?.audiences || []).map((a) => (
                  <button key={a.key} type="button" onClick={() => toggleAud(a.key)} data-testid={`bulk-aud-${a.key}`}
                    className={`text-xs rounded-full border px-2.5 py-1 ${audience.includes(a.key) ? "border-[#006090] bg-[#006090]/10 text-[#006090]" : "border-slate-200 text-slate-500"}`}>{a.label}</button>
                ))}
              </div>
            </F>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button className="bg-[#006090] hover:bg-[#004E75]" disabled={busy} data-testid="bulk-audience-confirm"
                onClick={() => post("/knowledge/bulk/metadata", { ids, changes: { audience } }, (d) => `Updated ${d.updated}`)}>{busy ? <InlineSpinner /> : "Apply"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetPreviewDialog({ onClose, onDone }) {
  const [inv, setInv] = useState(null);
  const [after, setAfter] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/knowledge/admin/preview-inventory").then(({ data }) => setInv(data)).catch((e) => toast.error(apiError(e))); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/knowledge/admin/preview-cleanup", { confirm: "RESET", reason: "Preview library reset before real uploads" });
      setAfter(data.after); toast.success(`Reset complete — ${data.before.sources} sources removed`); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const Row = ({ label, v }) => <div className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-500">{label}</span><span className="font-semibold tabular-nums">{v ?? 0}</span></div>;
  const counts = after || inv;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="knowledge-reset-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-1.5 text-rose-700"><ShieldAlert className="w-4 h-4" />Reset Preview Knowledge Library</DialogTitle></DialogHeader>
        {!inv ? <div className="py-6 flex justify-center"><InlineSpinner /></div> : (
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">{after ? "Cleanup complete. Current inventory:" : "This permanently removes the current Preview inventory below. Collections, taxonomy, RBAC, Assistant and Settings are kept. Production is never affected."}</p>
            <div className="rounded-lg border border-slate-200 p-3" data-testid="reset-inventory">
              <Row label="Sources" v={counts.sources} />
              <Row label="Physical files" v={counts.physical_files} />
              <Row label="Versions" v={counts.versions} />
              <Row label="File-history objects" v={counts.file_history_objects} />
              <Row label="Indexed records" v={counts.indexed_records} />
              <Row label="Assistant logs" v={counts.assistant_logs} />
            </div>
            {!after && (
              <>
                <F label={`Type RESET to confirm`}><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1" placeholder="RESET" data-testid="reset-confirm-input" /></F>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={onClose}>Cancel</Button>
                  <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy || confirm !== "RESET"} onClick={run} data-testid="reset-confirm-btn">{busy ? <InlineSpinner /> : "Permanently Reset Library"}</Button>
                </DialogFooter>
              </>
            )}
            {after && <DialogFooter><Button className="bg-[#006090] hover:bg-[#004E75]" onClick={onClose} data-testid="reset-done-btn">Done</Button></DialogFooter>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
